import './Orders.css';
import React, { useEffect, useState } from 'react';
import { useAppendedPaginatedResponse, useData } from '../../../model/data';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { serializeQuery } from '../../../model/filter';
import { useMeta } from '../../../model/meta';
import { getOrders, Order } from '../../../model/order';
import { OrdersList } from './OrdersList/OrdersList';
import { Icon } from '../../../components/Icon/Icon';
import { User } from '../../../model/user';
import { Company } from '../../../model/company';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { CreateOrder } from './CreateOrder';
import classnames from 'classnames';
import { PrintOrders } from './PrintOrders';
import { TagsUpdate, TagsUpdateModel } from './TagsUpdate';
import { PicklistPatch } from '../../../model/picklist';
import { useQueryAsState } from '../../../model/history';
import { FilterBar } from '../../../components/FilterBar/FilterBar';
import { t } from '../../../locales/translate';
import { compact } from 'lodash';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { FormSelect } from '../../../components/Form/Select/FormSelect';
import { Stack } from '../../../components/Stack/Stack';
import { LoadMorePagination } from '../../../components/load_more_pagination/load_more_pagination';
import { useModalPresenter } from '../../../components/Modal/PresentedModal';
import { OrderCount } from './order_count/order_count';

export function Orders(): JSX.Element {
  const [params, updateParams] = useQueryAsState();
  const modal = useModalPresenter();
  const paginator = useAppendedPaginatedResponse(
    (page, perPage) =>
      getOrders({
        term: serializeQuery(params.query),
        page,
        perPage,
        storage: params.storageLocation,
        status: params.status as unknown as string[],
        timeframe: params.timeframe,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
        withShippableProductsOnly: params.withShippableProductsOnly === 'true' ? true : false,
        archived: params.archived === 'true',
        paymentMethod: params.paymentMethod,
        productId: params.productId,
        productVariantId: params.productVariantId,
        venueSectionId: params.venueSectionId,
        itemStatus: params.itemStatus,
        containingOnly: params.containingOnly,
      }),
    JSON.stringify({ ...params, page: undefined })
  );

  useMeta({
    title: t.model.order._plural,
  });

  const orders = useData((db) => compact(paginator.currentIds.map((e) => db.table('orders').find(e))));

  // HANDLE SELECT
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  function handleOnRowSelect(row: Order): void {
    const exists = selectedOrders.find((item) => item.id === row.id);
    if (!exists) {
      setSelectedOrders([...selectedOrders, row]);
    } else {
      setSelectedOrders(selectedOrders.filter((item) => item.id !== row.id));
    }
  }

  function handleOnSelectAll(): void {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders);
    }
  }

  function selectedOrdersHaveDeliveries(): boolean {
    let valid = true;
    selectedOrders.forEach((order) => {
      if (!order.deliveries || order.deliveries.length === 0) {
        valid = false;
      }
      if (order.status === 'draft') {
        valid = false;
      }
    });
    return valid;
  }

  useEffect(() => {
    updateParams({ page: 0 });
  }, []);

  // HANDLE MODALS
  const [createModal, setCreateModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [tagsUpdateModal, setTagsUpdateModal] = useState(false);

  return (
    <Container className="orders">
      <Card
        title={
          selectedOrders.length > 0
            ? `${selectedOrders.length} ${
                selectedOrders.length > 1 ? t.model.order.selectedOrders : t.model.order.selectedOrder
              }`
            : t.model.order._plural
        }
        elevation={2}
        pagination={<LoadMorePagination paginator={paginator} />}
        actions={
          <>
            {selectedOrders.length === 0 && (
              <>
                {paginator.loading ? (
                  <Icon name="plus" loading />
                ) : (
                  paginator.alternateLink && (
                    <Stack size="xs" line align="center">
                      <a href={paginator.alternateLink} download>
                        <Tooltip reference={<Icon name="download-mail" />} darkMode>
                          {t.model.order.exportEmails}
                        </Tooltip>
                      </a>
                      <Tooltip
                        reference={
                          <Icon
                            name="document"
                            onClick={() =>
                              modal.present(OrderCount, {
                                ...(params.timeframe && {
                                  timeframe: {
                                    from: params.timeframe[0],
                                    to: params.timeframe[1],
                                  },
                                }),
                                ...(params.status && { status: params.status }),
                                ...(params.storageLocation && { storageLocationIds: params.storageLocation }),
                                ...(params.paymentMethod && { paymentMethodIds: params.paymentMethod }),
                                ...(params.containingOnly && { containingOnly: params.containingOnly }),
                                ...(params.withShippableProductsOnly && {
                                  withShippableProductsOnly: params.withShippableProductsOnly === 'true' ? true : false,
                                }),
                                ...(params.query && { term: serializeQuery(params.query) }),
                              })
                            }
                          />
                        }
                        darkMode
                      >
                        {t.model.order.showCount}
                      </Tooltip>
                    </Stack>
                  )
                )}
              </>
            )}
            {selectedOrders.length === 0 ? (
              <Icon
                name="plus"
                onClick={() => {
                  setCreateModal(true);
                }}
              />
            ) : (
              <>
                {selectedOrdersHaveDeliveries() && <Icon name="printer" onClick={() => setPrintModal(true)} />}
                <Icon name="flag" onClick={() => setTagsUpdateModal(true)} />
              </>
            )}
          </>
        }
        className={classnames({
          'orders-list-card--with-selected-orders': selectedOrders.length > 0,
        })}
        fullbleed={!paginator.error}
      >
        <Stack>
          <OrdersList
            orders={orders}
            selectedOrders={selectedOrders}
            onRowSelect={handleOnRowSelect}
            onSelectAll={handleOnSelectAll}
            error={paginator.error}
            loadingTable={paginator.loading}
            loadingMore={paginator.loadingMore}
            displayRowActions={false}
            actions={
              <FilterBar
                filterOrder={{
                  query: 0,
                  withShippableProductsOnly: 6,
                  includeArchived: 7,
                }}
                filters={[
                  'status',
                  'query',
                  'timeframe',
                  'storageLocation',
                  'withShippableProductsOnly',
                  'includeArchived',
                  'paymentMethod',
                ]}
                filterData={{
                  statusData: [
                    'draft',
                    'payment_pending',
                    'fulfillment',
                    'done',
                    'canceled',
                    'problem',
                    'refund_pending',
                    'incomplete',
                    'cart',
                    'pending_fulfillment',
                    'processing',
                  ],
                  sortByFrequencyId: 'orders',
                }}
                extraFilters={[
                  {
                    key: 'containingOnly',
                    order: 5,
                    renderer: (value, onChange) => {
                      return (
                        <FormSelect
                          style={{
                            minWidth: '200px',
                          }}
                          widthAuto
                          value={value}
                          onChange={(v) => {
                            onChange(v || undefined);
                          }}
                          label={t.model.order.containingOnly}
                          allowEmpty
                          options={[
                            {
                              value: 'online_ticket_only',
                              label: t.model.order.onlyContainingOptions.onlineTicketOnly,
                            },
                            {
                              value: 'hard_ticket_only',
                              label: t.model.order.onlyContainingOptions.hardTicketOnly,
                            },
                            {
                              value: 'merch_only',
                              label: t.model.order.onlyContainingOptions.merchOnly,
                            },
                            {
                              value: 'hard_ticket_and_merch',
                              label: t.model.order.onlyContainingOptions.hardTicketAndMerch,
                            },
                          ]}
                        />
                      );
                    },
                  },
                ]}
              />
            }
          />
        </Stack>
      </Card>
      {/* CREATE ORDER */}
      <ControlledModal<{ user: User | Company }>
        open={createModal}
        model={{} as { user: User | Company }}
        onClose={() => setCreateModal(false)}
        title={t.actions.create(t.model.order._name)}
        size="small"
      >
        {(controller) => <CreateOrder controller={controller} />}
      </ControlledModal>
      {/* CREATE ORDER */}
      <ControlledModal<PicklistPatch>
        open={printModal}
        model={{} as PicklistPatch}
        onClose={() => setPrintModal(false)}
        title={t.model.order.exportFulfillmentLists}
        size="small"
      >
        {(controller) => <PrintOrders orders={selectedOrders} controller={controller} />}
      </ControlledModal>
      {/* MASS UPDATE TAGS */}
      <ControlledModal<TagsUpdateModel>
        open={tagsUpdateModal}
        model={{} as TagsUpdateModel}
        onClose={() => setTagsUpdateModal(false)}
        title={t.model.order.massUpdateTags}
        size="small"
      >
        {(controller) => (
          <TagsUpdate
            orders={selectedOrders}
            controller={controller}
            onUpdate={() => {
              setSelectedOrders([]);
            }}
          />
        )}
      </ControlledModal>
    </Container>
  );
}
